import React, { createContext, ReactNode, useCallback, useContext, useEffect, useRef } from 'react';
import BinIcon from "../icon/Icon";
import { Offcanvas } from "react-bootstrap";

import { useSlider } from "./sliderHooks";
import {
    SliderIconWrapperStyled,
    SliderRowItemStyled,
    SliderToggleComponentWrapperStyled
} from "./styled/SliderStyled";
import classNames from "classnames";
import { OffcanvasPlacement } from "react-bootstrap/esm/Offcanvas";
import { StyledComponent } from "styled-components";
import useDragSensor, { IPosition } from "../../hooks/useDragSensor";

interface IStiderContext {
    closeSlider: () => void,
    closeSliderHandler: () => void,
    openSlider: () => void,
    isShowSlider: boolean
}

const SliderContext = createContext<IStiderContext>({
    closeSlider: () => {
    },
    closeSliderHandler: () => {
    },
    openSlider: () => {
    },
    isShowSlider: false
});

export const useSliderContext = () => {
    const context = useContext(SliderContext);
    if (!context) {
        throw new Error('SliderContext cannot be used outside the SliderContext provider')
    }
    return context;
}

interface ISliderCloseButtonProps {
    className?: string,
}

export const SliderCloseButton = (props: ISliderCloseButtonProps) => {
    const { className } = props;
    const { closeSlider } = useSliderContext();
    return <SliderIconWrapperStyled onClick={closeSlider} className={className}>
        <BinIcon name="IcClose" height="24px" width="24px"/>
    </SliderIconWrapperStyled>
}

export type TSliderToggleComponent = ((props: {onClick: () => void}) => JSX.Element) | JSX.Element

interface ISliderProps {
    backdropClassName?: string,
    children: JSX.Element | JSX.Element[] | ReactNode,
    headerContentComponent?: JSX.Element,
    onClose?: () => void,
    placement?: OffcanvasPlacement,
    show?: boolean,
    showCloseButton?: boolean,
    style?: React.CSSProperties,
    swipeDownToClose?: boolean,
    toggleComponent?: TSliderToggleComponent,
    toggleWrapperClassName?: string,
    popUpClassName?: string,
}

export const Slider = ({
                           backdropClassName,
                           children,
                           headerContentComponent,
                           onClose,
                           placement = 'end',
                           show,
                           showCloseButton = true,
                           style,
                           swipeDownToClose,
                           toggleComponent,
                           toggleWrapperClassName,
                           popUpClassName,
                       }: ISliderProps) => {
    const { closeSlider, isShowSlider, openSlider } = useSlider();

    useEffect(() => {
        if (show === undefined) {
            return;
        }
        if (show && !isShowSlider) {
            openSlider();
        }
        if (!show && isShowSlider) {
            closeSlider();
        }
    }, [closeSlider, isShowSlider, openSlider, show]);

    const closeSliderHandler = useCallback(() => {
        if (onClose) {
            onClose();
        }
        closeSlider();
    }, [closeSlider, onClose]);


    const { sensorRef } = useDragSensor({
        onDragStart: (event, initial, node) => {
            if (node) {
                node.style.transition = 'none 0s ease 0s';
            }
        },
        onDrag: (offset: IPosition, initial: IPosition, node) => {
            if (node) {
                node.style.transform = `translateY(${Math.max(offset?.y - initial?.y, 0)}px)`;
            }
        },
        onDragEnd: (ev, offset, initial, node) => {
            if (node) {
                node.style.transition = '';
                node.style.transform = '';
            }
            if (offset.y - initial.y > 50) {
                closeSliderHandler();
            }
        },
    });
    const offcanvasRef = useRef(null);


    useEffect(() => {
        if (isShowSlider && offcanvasRef?.current && offcanvasRef.current.dialog && swipeDownToClose) {
            if (typeof sensorRef === 'function') {
                sensorRef(offcanvasRef.current.dialog);
            }
        }
    }, [sensorRef, offcanvasRef, isShowSlider, showCloseButton, swipeDownToClose])

    return <SliderContext.Provider value={{ closeSlider, closeSliderHandler, openSlider, isShowSlider }}>
        {!!toggleComponent &&
            (typeof toggleComponent === 'function' ? toggleComponent({onClick: openSlider}) : <SliderToggleComponentWrapperStyled
                onClick={openSlider}
                className={`slider-toggle-component-wrapper ${toggleWrapperClassName || ''}`}
            >
                {toggleComponent}
            </SliderToggleComponentWrapperStyled>)}
        <Offcanvas
            show={isShowSlider}
            ref={offcanvasRef}
            onHide={closeSliderHandler}
            placement={placement}
            backdropClassName={backdropClassName}
            className={popUpClassName}
            style={{ width: "100%", ...style }}>
            {
                swipeDownToClose && <div className="offcanvas-grabber">
                    <BinIcon name={'IcGrabber'} size={32}/>
                </div>
            }
            {
                (showCloseButton || headerContentComponent) && <div className="offcanvas-header">
                    {showCloseButton && <SliderCloseButton className={'mt-3'}/>}
                    {headerContentComponent}
                </div>
            }
            {children}
        </Offcanvas>
    </SliderContext.Provider>
}

type ISliderRowProps = React.ComponentProps<StyledComponent<"div", any, {}, never>>;

const SliderRow = ({ children, onClick, disabled, className, ...props }: ISliderRowProps) => {
    const { closeSlider } = useSliderContext();
    const handleClick = useCallback((event) => {
        closeSlider();
        if (onClick) {
            onClick(event);
        }
    }, [closeSlider, onClick])
    return <SliderRowItemStyled
        onClick={handleClick}
        className={classNames(className, { 'disabled': disabled })}
        {...props}
    >
        {children}
    </SliderRowItemStyled>
}

const SliderCloseWrapper = ({ children }: { children?: JSX.Element }) => {
    const { closeSlider } = useSliderContext();

    const handleSliderClick = useCallback(() => {
        closeSlider();
    }, [closeSlider]);

    return <div onClick={handleSliderClick}>
        {children}
    </div>
}

Slider.Row = SliderRow;
Slider.CloseButton = SliderCloseButton;
Slider.CloseWrapper = SliderCloseWrapper;

